import { Theme, createStyles, withStyles, WithStyles, AppBar, Button, Grid, Toolbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Drawer, useTheme, Typography, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import * as React from 'react';
import clsx from 'clsx';
import { Operations } from './NavigatorData';
import { OperationContext } from './OperationContext';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeltaResult, { ViewType } from '../DeltaResult';
import { FileTabNames } from './FilesContent';
import GetAppIcon from '@material-ui/icons/GetApp';
import { ArrayAlignment, ThreeWayMergeMode, MergePriority, PatchDirection, GraftResolutionMode } from '../api/commonConfig';
import JobMetadata from './JobMetadata';
import { ErrorInfo } from '../api/commonAction';
import { DeltaXMLColors, DeltaJSONColors } from '../styling/DeltaColors';

const drawerWidth = 840;
const adjustment = 260;
const totalRootWidth = drawerWidth + adjustment;

const dummy = () => { };

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            maxWidth: `calc(100vw - ${totalRootWidth}px)`,
        },
        content: {
            flexGrow: 1,
            padding: '0px',
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginRight: -drawerWidth,
        },
        contentShift: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginRight: 0,
        },
        paper: {
            maxWidth: 936,
            margin: 'auto',
            overflow: 'hidden',
        },
        searchBar: {
            borderBottom: '1px solid '+ DeltaJSONColors.MERCURY,
            backgroundColor: DeltaXMLColors.CONCRETE,
        },
        contentWrapper: {
            margin: '0px',
        },
        button: {
            margin: theme.spacing(1),
        },
        tableContainer: {
            maxHeight: 'calc(100vh - 150px)',
        },
        fileNameLink: {
            textDecoration: 'underline',
            color: 'primary',
            cursor: 'pointer',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: '14px'
        },
        fileNameLinkInactive: {
            textDecoration: 'none',
            color: 'primary',
            cursor: 'default',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: '14px'
        },
        downloadIcon: {
            color: DeltaXMLColors.FOUNTAIN_BLUE,
            marginRight: '4px',
            fontSize: '14px'
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        drawerPaper: {
            width: drawerWidth,
            top: '96px'
        },
        drawerHeader: {
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(0, 1),
            // necessary for content to be below app bar
            ...theme.mixins.toolbar,
            justifyContent: 'flex-start',
            height: '52px',
            backgroundColor: DeltaXMLColors.CONCRETE,
        },
        toolbar: {
            padding: '0px'
        },
        iconButton: {
            float: 'right',
            margin: '5px 0px 5px 0px'
        },
        tableHeadCell: {
            backgroundColor: DeltaXMLColors.CELERY,
            color: DeltaJSONColors.WHITE,
            padding: '8px',
            fontWeight: 600
        },
        gridItemCentered: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        filename: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        errorinRow: {
            color: DeltaXMLColors.RED + '!important'
        }
    });

export interface JobsContentProps extends WithStyles<typeof styles> {
    operationType: Operations;
    jobsData: AllJobs;
    jobInfo: JobsContentInfo;
    jobsRequestStatus: JobsRequestStatus;
    handleJobClick: (row: JobData) => void;
    handleDrawerVisibility: (close: boolean) => void;
    downloadResult: (jobData?: JobData) => void;
}

export interface JobParameters {
    inputA?: string | undefined,
    inputB?: string | undefined,
    ancestor?: string | undefined,
    inputAKBytes?: number | undefined,
    inputBKBytes?: number | undefined,
    ancestorKBytes?: number | undefined,
    arrayAlignmentOption: ArrayAlignment | undefined,
    wordByWordOption: boolean | undefined,
    threeWayMergeModeOption: ThreeWayMergeMode | undefined,
    mergePriorityOption: MergePriority | undefined,
    patchDirectionOption: PatchDirection | undefined,
    graftResolutionModeOption: GraftResolutionMode | undefined,
}

export class JobParametersW implements JobParameters {
    inputA?: string | undefined;
    inputB?: string | undefined;
    ancestor?: string | undefined;
    inputAKBytes?: number | undefined;
    inputBKBytes?: number | undefined;
    ancestorKBytes?: number | undefined;
    arrayAlignmentOption: ArrayAlignment | undefined;
    wordByWordOption: boolean | undefined;
    threeWayMergeModeOption: ThreeWayMergeMode | undefined;
    mergePriorityOption: MergePriority | undefined
    patchDirectionOption: PatchDirection | undefined;
    graftResolutionModeOption: GraftResolutionMode | undefined

    set inputASet(value: string | undefined) {
        if (value !== undefined) {
            this.inputA = value;
        } else {
            this.inputA = '';
        }
    }
    set inputBSet(value: string | undefined) {
        if (value !== undefined) {
            this.inputB = value;
        } else {
            this.inputB = '';
        }
    }
    set ancestorSet(value: string | undefined) {
        if (value !== undefined) {
            this.ancestor = value;
        } else {
            this.ancestor = ''
        }
    }
    set inputASizeSet(value: number | undefined) {
        if (value !== undefined) {
            this.inputAKBytes = value;
        } else {
            this.inputAKBytes = 0;
        }
    }
    set inputBSizeSet(value: number | undefined) {
        if (value !== undefined) {
            this.inputBKBytes = value;
        } else {
            this.inputBKBytes = 0;
        }
    }
    set ancestorSizeSet(value: number | undefined) {
        if (value !== undefined) {
            this.ancestorKBytes = value;
        } else {
            this.ancestorKBytes = 0
        }
    }
    set arrayAlignmentOptionSet(value: any) {
        if (value !== undefined) {
            this.arrayAlignmentOption = value;
        } else {
            this.arrayAlignmentOption = ArrayAlignment.orderless;
        }
    }
    set wordByWordOptionSet(value: any) {
        if (value !== undefined) {
            this.wordByWordOption = value;
        } else {
            this.wordByWordOption = false;
        }
    }
    set threeWayMergeModeOptionSet(value: any) {
        if (value !== undefined) {
            this.threeWayMergeModeOption = value;
        } else {
            this.threeWayMergeModeOption = ThreeWayMergeMode.resolveUsingEdit1;
        }
    }
    set mergePriorityOptionSet(value: any) {
        if (value !== undefined) {
            this.mergePriorityOption = value;
        } else {
            this.mergePriorityOption = MergePriority.a;
        }
    }
    set patchDirectionOptionSet(value: any) {
        if (value !== undefined) {
            this.patchDirectionOption = value;
        } else {
            this.patchDirectionOption = PatchDirection.aTob;
        }
    }
    set graftResolutionModeOptionSet(value: any) {
        if (value !== undefined) {
            this.graftResolutionModeOption = value;
        } else {
            this.graftResolutionModeOption = GraftResolutionMode.changesetPriority;
        }
    }
}

export enum JobsRequestStatus {
    notYetRequested = 'Jobs history not requested',
    jobsFetchInProgress = 'Refreshing jobs history...',
    resultFetchInProgress = 'Fetching result...',
    jobsOk = '',
    failedToFetch = 'Error encountered when fetching jobs history',
    failedToLoadResult = 'Error encountered when loading result',
    errorInJobFetched = 'Error encountered while running operation'
}

export interface JobData {
    jobId: string,
    jobLabel: string,
    jobStatus: string,
    timeCreate: string,
    jobDuration: number,
    resultFilename: string,
    resultContents?: any,
    error?: ErrorInfo,
    parameters: JobParameters
}

export class AllJobs {
    [Operations.compare]: JobData[] = [];
    [Operations.threeWayMerge]: JobData[] = [];
    [Operations.twoWayMerge]: JobData[] = [];
    [Operations.graft]: JobData[] = [];
    [Operations.patch]: JobData[] = [];
}

function createData(jobId: string, jobLabel: string, jobStatus: string, timeCreate: string, jobDuration: number, resultFilename: string, resultContents: any, error: ErrorInfo, parameters: JobParameters): JobData {
    return { jobId, jobLabel, jobStatus, timeCreate, jobDuration, resultFilename, resultContents, error, parameters };
}

export class JobsContentInfo {
    public selectedJob: JobData | null = null;
    public editorOpen: boolean = false;
}

interface Column {
    id: 'jobLabel' | 'timeCreate' | 'jobDuration' | 'resultFilename';
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: number) => string;
}

const fullColumns: Column[] = [
    { id: 'jobLabel', label: 'Description', minWidth: 240 },
    { id: 'timeCreate', label: 'Created', minWidth: 120 },
    {
        id: 'jobDuration',
        label: 'Time (s)',
        minWidth: 100,
        format: (value: number) => {
            const jobDurationInSec = (value / 1000);
            const minDurationInSec = jobDurationInSec < 0.1 ? 0.1 : jobDurationInSec;
            return minDurationInSec.toFixed(1);
        }
    },
    {
        id: 'resultFilename',
        label: 'Filename',
        minWidth: 220
    }
];

const reducedColumns = fullColumns.filter((column) => {
    return column.id === 'jobLabel' || column.id === 'timeCreate'
});

const fullColgroups: JSX.Element = (
    <colgroup>
        <col width="20%" />
        <col width="20%" />
        <col width="20%" />
        <col width="20%" />
    </colgroup>
);

const reducedColgroups: JSX.Element = (
    <colgroup>
        <col width="50%" />
        <col width="50%" />
    </colgroup>
);

function formatDate(dateString: string) {
    const dateObj = new Date(dateString);

    const YYYY = dateObj.getFullYear();
    // when calling getMonth, January is 0
    const MM = padNumber(dateObj.getMonth() + 1);
    const DD = padNumber(dateObj.getDate());

    const hh = padNumber(dateObj.getHours());
    const mm = padNumber(dateObj.getMinutes());
    const ss = padNumber(dateObj.getSeconds());


    return `${YYYY}-${MM}-${DD} ${hh}:${mm}:${ss}`;
}

function padNumber(number: number) {
    return number < 10 ? `0${number}` : number;
}


function JobsContent(props: JobsContentProps) {
    const opContext = React.useContext(OperationContext);
    const { classes, operationType, jobsData, jobInfo, jobsRequestStatus, handleJobClick, handleDrawerVisibility, downloadResult } = props;
    const columns = jobInfo.editorOpen ? reducedColumns : fullColumns;
    const colgroups = jobInfo.editorOpen ? reducedColgroups : fullColgroups;

    opContext.handleConfigOperationChange(operationType);
    const sessionJobsData = jobsData[opContext.operationType];

    const preferredDeltaViewType: ViewType = opContext.operationType === Operations.graft || opContext.operationType === Operations.patch ? ViewType.Raw : ViewType.DiffResult;

    let jobTableRows: JobData[] = [];
    for (var i = 0; i < sessionJobsData.length; i++) {
        const jobData: JobData = sessionJobsData[i];
        jobTableRows.push(createData(jobData.jobId, jobData.jobLabel, jobData.jobStatus, formatDate(jobData.timeCreate), jobData.jobDuration, jobData.resultFilename, jobData.resultContents, jobData.error!, jobData.parameters));
    }

    const handleDownloadOnFilenameClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, jobData: JobData) => {
        downloadResult(jobData);
        event.stopPropagation();
    }

    const createCellContents = (row: JobData, column: Column) => {
        const value = row[column.id];
        const formattedValue = column.format && typeof value === 'number' ? column.format(value) : value;
        if (column.id === 'resultFilename') {
            return (
                <React.Fragment>
                    <Typography variant="body2" color="primary" className={row.error ? classes.fileNameLinkInactive : classes.fileNameLink} onClick={(event) => handleDownloadOnFilenameClick(event, row)}>
                        <GetAppIcon className={classes.downloadIcon} />{formattedValue}
                    </Typography>
                </React.Fragment>
            )
        } else {
            return formattedValue;
        }
    }

    function getCodeForJob(job: JobData | null) {
        if (job === null) {
            return '{}'
        } else {
            if (job.error) {
                return JSON.stringify("Error " + job.error.errorCode + ": " + job.error.errorMessage);
            } else {
                if (preferredDeltaViewType === ViewType.Raw) {
                    return job.resultContents ? JSON.stringify(job.resultContents, null, 4) : '';
                } else {
                    return job.resultContents ? job.resultContents : {};
                }
            }
        }
    }

    const theme = useTheme();

    const jobTableElement = (jobInfo.selectedJob) ? <JobMetadata jobData={jobInfo.selectedJob} /> : <></>

    const [contentWidth, setContentWidth] = React.useState(window.innerWidth);
    const [openDialog, setOpenDialog] = React.useState(false);

    const handleClose = () => {
        setOpenDialog(false);
    };

    const updateBrowserWidth = () => {
        setContentWidth(window.innerWidth);
        if (contentWidth <= 1400) {
            handleDrawerVisibility(false);
        } else {
            handleDrawerVisibility(true);
        }
    }

    React.useEffect(() => {
        window.addEventListener("resize", updateBrowserWidth);
        return () => window.removeEventListener("resize", updateBrowserWidth);
    });

    const handleJobClickOnContentWidth = (row: JobData) => {
        if (contentWidth <= 1100) {
            setOpenDialog(true);
            handleDrawerVisibility(false);
        } else {
            handleJobClick(row);
            handleDrawerVisibility(true);
        }
    }

    return (
        <OperationContext.Consumer>
            {context => context && (
                <div className={classes.root}>
                    <div className={clsx(classes.content, {
                        [classes.contentShift]: jobInfo.editorOpen,
                    })}>
                        <AppBar
                            id={operationType.toString()}
                            className={classes.searchBar}
                            position="static"
                            elevation={0}>
                            <Toolbar className={classes.toolbar}>
                                <Grid container spacing={1}>
                                    <Grid item xs={1} className={classes.gridItemCentered}>
                                        {(jobsRequestStatus === JobsRequestStatus.jobsFetchInProgress || jobsRequestStatus === JobsRequestStatus.resultFetchInProgress) ? <CircularProgress variant="indeterminate" /> : <></>}
                                    </Grid>
                                    <Grid item xs={11} className={classes.gridItemCentered}>
                                        <Typography variant="body2" color="primary" className={((jobsRequestStatus === JobsRequestStatus.failedToFetch || jobsRequestStatus === JobsRequestStatus.failedToLoadResult || jobsRequestStatus === JobsRequestStatus.errorInJobFetched) ? classes.errorinRow : "")}>
                                            {(jobsRequestStatus !== JobsRequestStatus.failedToLoadResult && jobsRequestStatus !== JobsRequestStatus.resultFetchInProgress && jobsRequestStatus !== JobsRequestStatus.errorInJobFetched) ? jobsRequestStatus : ''}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Toolbar>
                        </AppBar>
                        <div className={classes.contentWrapper} >
                            <TableContainer className={classes.tableContainer}>
                                <Table stickyHeader aria-label="sticky table" style={{ tableLayout: 'fixed' }}>
                                    {colgroups}
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.align}
                                                    className={classes.tableHeadCell}
                                                >
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {jobTableRows.map((row) => {
                                            return (
                                                <TableRow selected={jobInfo.selectedJob?.jobId === row.jobId} role="checkbox" tabIndex={-1} key={row.jobId} onClick={() => handleJobClickOnContentWidth(row)}>
                                                    {columns.map((column) => {
                                                        return (
                                                            <TableCell className={row.error ? classes.errorinRow : ''} key={column.id} align={column.align} style={{ padding: '8px' }}>
                                                                {createCellContents(row, column)}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                        <Dialog onClose={handleClose} open={openDialog}>
                            <DialogTitle style={{backgroundColor: DeltaXMLColors.OXFORD_BLUE, color: DeltaXMLColors.CELERY}}>
                                <Typography variant="h5">Warning!</Typography>
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="body1" color="secondary">Please increase width of browser to preview result</Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose} color="primary" variant="contained" className={classes.button}>
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Drawer
                            className={classes.drawer}
                            variant="persistent"
                            anchor="right"
                            open={jobInfo.editorOpen}
                            classes={{
                                paper: classes.drawerPaper,
                            }}
                        >
                            <Grid container className={classes.drawerHeader}>
                                <Grid item xs={1}>
                                    <IconButton onClick={() => handleDrawerVisibility(false)}>
                                        {theme.direction === 'rtl' ? <ChevronLeftIcon style={{color: DeltaXMLColors.OXFORD_BLUE}}/> : <ChevronRightIcon style={{color: DeltaXMLColors.OXFORD_BLUE}}/>}
                                    </IconButton>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography className={classes.filename} variant="h6" color="primary">{jobInfo.selectedJob?.resultFilename}</Typography>
                                </Grid>
                                <Grid item xs={5}>
                                    <Typography variant="body2" color="primary" className={(jobsRequestStatus === JobsRequestStatus.failedToLoadResult || jobsRequestStatus === JobsRequestStatus.errorInJobFetched) ? classes.errorinRow : ""}>{(jobsRequestStatus === JobsRequestStatus.failedToLoadResult || jobsRequestStatus === JobsRequestStatus.resultFetchInProgress || jobsRequestStatus === JobsRequestStatus.errorInJobFetched) ? jobsRequestStatus : ''}</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.button}
                                        endIcon={<GetAppIcon />}
                                        onClick={() => downloadResult(jobInfo.selectedJob!)}
                                        disabled={jobInfo.selectedJob?.error ? true : false}>
                                        Download
                                        </Button>
                                </Grid>
                            </Grid>
                            <DeltaResult
                                standaloneEditor={true}
                                display={true}
                                onUnmount={dummy}
                                onTextChange={dummy}
                                editorId={FileTabNames.rawOutTab}
                                code={getCodeForJob(jobInfo.selectedJob)}
                                viewType={preferredDeltaViewType}
                                info={'info'}
                                jobMetadataTable={jobTableElement}
                                inActiveGroup={true}
                                onDidGetFocus={dummy}
                                editorModel={null}
                                monacoTheme={'light'} />
                        </Drawer>
                    </div>
                </div>
            )}
        </OperationContext.Consumer>
    );
}


export default withStyles(styles)(JobsContent);